import { Box, type SxProps, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { CHANGE_HERO, FONT_SIZE, LETTER_SPACING, RADIUS } from "@/theme/tokens";
import { ShiftWindowIcon } from "@/ui/Icons/ShiftWindowIcon";

type Props = {
	count: number;
	/** "Sat, Sep 26". */
	date: string;
	/** "7:00 AM–7:00 PM". */
	hours: string;
	/** Day (0) / night (1) slot of the window, for the sun / moon. */
	shiftIndex: number;
	/** Already-translated location, or `null` where the panel has no location picker. */
	location: string | null;
};

/** The brand-washed band: hairline brand edge and a diagonal wash from the leading corner. */
const HERO_SX: SxProps<Theme> = (theme) => ({
	display: "flex",
	flexWrap: "wrap",
	alignItems: "flex-end",
	justifyContent: "space-between",
	columnGap: 3,
	rowGap: 1.25,
	px: 2.5,
	py: 2,
	borderRadius: `${RADIUS.CARD}px`,
	border: `1px solid ${alpha(theme.palette.primary.main, CHANGE_HERO.BORDER_ALPHA)}`,
	// Diagonal wash from the leading corner: depth under the number without tinting the whole band.
	background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, CHANGE_HERO.WASH_ALPHA)} 0%, ${alpha(theme.palette.primary.main, 0)} ${CHANGE_HERO.WASH_STOP})`,
});

/** "ADDING TO THE SHIFT · 3 staff" — the eyebrow over the headline count. */
const HeroCount: FC<{ count: number }> = ({ count }) => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
			<Typography
				component="span"
				sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: "primary.dark" }}
			>
				{t("add.staff.review.eyebrow")}
			</Typography>
			<Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
				<Typography
					component="span"
					data-testid="add-staff-review-hero-count"
					sx={{
						fontSize: FONT_SIZE.STAT_VALUE,
						fontWeight: 700,
						lineHeight: 1,
						letterSpacing: LETTER_SPACING.DISPLAY,
						fontVariantNumeric: "tabular-nums",
						color: "text.primary",
					}}
				>
					{count.toLocaleString()}
				</Typography>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 500, color: "text.secondary" }}>
					{t("add.staff.review.staff", { count })}
				</Typography>
			</Box>
		</Box>
	);
};

/** "Sat, Sep 26 ☀ 7:00 AM–7:00 PM" over the location, right-aligned. */
const HeroWhere: FC<Omit<Props, "count">> = ({ date, hours, shiftIndex, location }) => (
	<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25, minWidth: 0, textAlign: "end" }}>
		<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.EMPHASIS, whiteSpace: "nowrap" }}>
			<Box component="span" sx={{ color: "text.secondary" }}>
				{date}
			</Box>
			<ShiftWindowIcon shiftIndex={shiftIndex} labelled />
			<Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
				{hours}
			</Box>
		</Box>
		{location && (
			<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }}>
				{location}
			</Typography>
		)}
	</Box>
);

/**
 * The review's one focal moment: how many staff this adds and exactly where — the same read-back the
 * footer showed, lifted onto the brand wash the bulk-change review uses, so the warnings below read as
 * "about this add" rather than a list of problems floating on their own.
 */
export const MultiAddReviewHero: FC<Props> = ({ count, ...where }) => (
	<Box data-testid="add-staff-review-hero" sx={HERO_SX}>
		<HeroCount count={count} />
		<HeroWhere {...where} />
	</Box>
);
